import React, { useEffect, useState } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";
import { icon } from "../../../../config";
import { getFileExtension } from "../../../TaskSidebar/TaskSidebarContent/Attachments/attachments.helper";
import { downloadFile } from "../../../TaskSidebar/TaskSidebarContent/Attachments/attachments.helper";
import "./FileUploadRenderer.scss";
import TableFileUploadModal from "./TableFileUploadModal";

const formatFileSize = (size) => {
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUploadRenderer = ({ element, onChange, isInteractive = false, showResponses = false, uploadedAttachments = [] }) => {
  const { properties = {}, value = [] } = element;
  const { acceptedFileTypes = "", multiple = true, maxFileSize = 10485760, disabled = false } = properties;

  const [modalOpen, setModalOpen] = useState(false);

  // files[] is the working list during form filling; each entry is
  // { name, size, source: "device"|"cms", rawFile?, attachmentKey?, id?, uid?, pendingDelete? }
  const [files, setFiles] = useState(Array.isArray(value) ? value : value ? [value] : []);

  useEffect(() => {
    if(isInteractive) return;
    setFiles([...files, ...uploadedAttachments]);
  }, [uploadedAttachments]);

  const existingAttachments =
    uploadedAttachments && Array.isArray(uploadedAttachments) ? uploadedAttachments : uploadedAttachments ? [uploadedAttachments] : [];

  const handleFileSelect = (fileOrFiles, autoRemove) => {
    const incoming = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    const entries = incoming.map((file) => (autoRemove && file?.source === "cms" ? { ...file, pendingDelete: true } : file));
    const updated = multiple ? [...files, ...entries] : [entries[0]];
    setFiles(updated);
    if (onChange) onChange(updated);
  };

  const handleRemove = (index) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    if (onChange) onChange(updated);
  };

  // ── Read-only response view ───────────────────────────────────
  if (showResponses && !isInteractive) {
    const hasAny = existingAttachments.length > 0;

    return (
      <div className='file-upload-renderer mb-3'>
        <div className='uploaded-files-display'>
          {hasAny ? (
            <ul className='file-list p-0 m-0'>
              {existingAttachments.map((file, index) => {
                const displayName = file?.filename ?? file?.name ?? `File ${index + 1}`;
                return (
                  <li key={`attachment-${index}`} className='file-item d-flex align-items-center justify-content-between p-3'>
                    <div className='file-info d-flex align-items-center flex-grow-1'>
                      <span className='file-icon d-flex align-items-center justify-content-center me-3'>
                        <FileIcon extension={getFileExtension(displayName)} {...defaultStyles.png} />
                      </span>
                      <div className='file-details d-flex flex-column'>
                        <span className='file-name mb-1'>{displayName}</span>
                        {file?.size > 0 && <span className='file-size'>{formatFileSize(file?.size)}</span>}
                      </div>
                    </div>
                    {file?.url && (
                      <a href={file?.url} target='_blank' rel='noopener noreferrer' className='download-link p-2' title='Download file'>
                        {icon.DOWNLOAD_FILE}
                      </a>
                    )}
                    {!file?.url && file?.attachmentKey && (
                      <button
                        type='button'
                        className='download-link p-2 blue'
                        title='Download file'
                        onClick={() => downloadFile(null, displayName, file.attachmentKey)}>
                        {icon.DOWNLOAD_FILE}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className='text-muted d-block text-center py-4 px-3'>No files uploaded</div>
          )}
        </div>
      </div>
    );
  }

  // ── Interactive / editable view ───────────────────────────────
  return (
    <div className='file-upload-renderer mb-3'>
      {files.length > 0 && (
        <ul className='fur-file-list'>
          {files.map((file, index) => (
            <li key={file.uid || index} className='fur-file-item'>
              <span className='fur-file-icon'>{icon.FORM_ICON}</span>
              <div className='fur-file-details'>
                <span className='fur-file-name' title={file.name || file.filename}>
                  {file.name || file.filename}
                </span>
                {file.size > 0 && <span className='fur-file-size'>{formatFileSize(file.size)}</span>}
              </div>
              {!disabled && (
                <button type='button' className='fur-remove-btn' title='Remove file' onClick={() => handleRemove(index)}>
                  {icon.DELETE_ICON}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (multiple || files.length === 0) && (
        <button type='button' className='fur-upload-btn' onClick={() => setModalOpen(true)}>
          {icon.UPLOAD}&nbsp; {files.length > 0 ? "Upload More Files" : "Upload File"}
        </button>
      )}

      {modalOpen && (
        <TableFileUploadModal
          onSelect={handleFileSelect}
          onClose={() => setModalOpen(false)}
          multiple={multiple}
          acceptedFileTypes={acceptedFileTypes}
          maxFileSize={maxFileSize}
        />
      )}
    </div>
  );
};

export default FileUploadRenderer;
